import { CO_SERVICES, type ContentOptimization, type CoAssortmentRow, type CoService } from "../schema";

// The Content Optimization cart: ASIN -> services ticked. Pure functions the
// assortment page and (later) the checkout share. Persisted server-side in
// the room state sidecar under `coSelection` (one cart per room).
export type CoSelection = Record<string, CoService[]>;

// Default per-ASIN rates (Eric: 2026-09-23, copy and images 2026-09-24); a room stores its own in
// `contentOptimization.rates` and the provisioning skill may override them.
export const DEFAULT_CO_RATES: Record<CoService, number> = {
    copy: 25,
    images: 25,
    infographics: 25,
    aplus: 25,
};

export const CO_SERVICE_LABELS: Record<CoService, string> = {
    copy: "Copy",
    images: "Images",
    infographics: "Infographics",
    aplus: "A+ content",
};

// Hover text on a service's column header, where the label alone is not enough (Eric, 2026-09-24).
export const CO_SERVICE_HINTS: Partial<Record<CoService, string>> = {
    copy: "Copy includes the title, bullets, description, and back-end keywords.",
};

export interface BulkRule {
    /** Tick a cell when its own dimension score is below this (0-100). */
    threshold: number;
    /** Leave the N highest-revenue ASINs alone. */
    skipTop: number;
    /** Leave the N lowest-revenue ASINs alone. */
    skipBottom: number;
    /** Service columns turned off in the header; the rule ticks nothing in them. */
    off: CoService[];
    /** Leave out an ASIN whose ticked services cost more than its T12M sales (2026-10-05, Eric). */
    skipCostly?: boolean;
    /** Tick a service whose own score is in this range, ends included (2026-10-05, Eric: replaces the
     *  threshold slider). A rule saved before then has no range and reads as 0 to its threshold. */
    hygieneMin?: number;
    hygieneMax?: number;
}

// The score range a rule ticks: the saved range, or 0 to the legacy threshold.
export function scoreRange(rule: BulkRule): [number, number] {
    return [rule.hygieneMin ?? 0, rule.hygieneMax ?? rule.threshold];
}

export const DEFAULT_BULK_RULE: BulkRule = { threshold: 80, skipTop: 0, skipBottom: 0, off: [], skipCostly: false, hygieneMin: 0, hygieneMax: 80 };

// What a set of services costs for one ASIN at the room's rates.
export function servicesCost(services: readonly CoService[], rates: ContentOptimization["rates"] | undefined): number {
    const r = { ...DEFAULT_CO_RATES, ...(rates ?? {}) };
    return services.reduce((sum, s) => sum + r[s], 0);
}

// ASINs ranked by trailing-12-month revenue, highest first. A row with no
// attributable revenue ranks below every row with one, so it is never in the
// top band and always counts toward the bottom band.
export function rankByRevenue(rows: readonly CoAssortmentRow[]): string[] {
    return [...rows]
        .sort((a, b) => (b.revenue12mo ?? -1) - (a.revenue12mo ?? -1))
        .map((r) => r.asin);
}

// The selection the bulk rule produces. A cell is ticked when the row is
// inside the band and the cell's score is known and below the threshold. A
// missing element scores 0 and is ticked; a TBD score (null, present but not
// evaluated yet) is never ticked by the rule, only by hand.
// Cells already on a placed order, as "asin:service" keys. They stay ticked
// and disabled in the matrix, the bulk rule skips them and a hand tick
// cannot change them.
export function lockedCells(orders: readonly { cells: Record<string, string[]> }[]): Set<string> {
    const out = new Set<string>();
    for (const o of orders) for (const [asin, services] of Object.entries(o.cells)) for (const s of services) out.add(`${asin}:${s}`);
    return out;
}

// The services stack (Eric, 2026-09-25): each one needs every service before
// it in CO_SERVICES, so Images brings Copy, Infographics brings Copy and
// Images, and A+ brings all three. A cell already at 100 can't be ticked and
// never blocks the chain; a cell on a placed order is left to its order.
export function tickable(row: CoAssortmentRow, service: CoService): boolean {
    const score = row.scores[service];
    return score === null || score === undefined || score < 100;
}

export function stackServices(row: CoAssortmentRow, services: readonly CoService[], locked: ReadonlySet<string> = new Set()): CoService[] {
    const top = Math.max(-1, ...services.map((s) => CO_SERVICES.indexOf(s)));
    return CO_SERVICES.filter((s, i) => i <= top && tickable(row, s) && !locked.has(`${row.asin}:${s}`));
}

// A header tick is select all / select none for its column (Eric, 2026-09-28;
// until then it switched the column in the filter and rebuilt the whole cart).
// On ticks every tickable, unordered cell in the column (the stack brings the
// services before it); off clears the column (and the services after it).
// Returns the per-ASIN patch; other hand ticks are left as they are.
export function setColumn(selection: CoSelection, rows: readonly CoAssortmentRow[], service: CoService, on: boolean, locked: ReadonlySet<string> = new Set()): Record<string, CoService[]> {
    const patch: Record<string, CoService[]> = {};
    for (const row of rows) {
        const current = selection[row.asin] ?? [];
        if (current.includes(service) === on || locked.has(`${row.asin}:${service}`)) continue;
        const next = toggleCell({ [row.asin]: current }, row, service, locked)[row.asin] ?? [];
        if (next.join() !== current.join()) patch[row.asin] = next;
    }
    return patch;
}

// The header tick's state: checked when every tickable, unordered cell in the
// column is in the cart, indeterminate when some are.
export function columnState(selection: CoSelection, rows: readonly CoAssortmentRow[], service: CoService, locked: ReadonlySet<string> = new Set()): "all" | "some" | "none" {
    let eligible = 0;
    let picked = 0;
    for (const row of rows) {
        if (!tickable(row, service) || locked.has(`${row.asin}:${service}`)) continue;
        eligible += 1;
        if (selection[row.asin]?.includes(service)) picked += 1;
    }
    return eligible > 0 && picked === eligible ? "all" : picked > 0 ? "some" : "none";
}

export function applyBulkRule(rows: readonly CoAssortmentRow[], rule: BulkRule, locked: ReadonlySet<string> = new Set(), rates?: ContentOptimization["rates"]): CoSelection {
    const services = CO_SERVICES.filter((s) => !rule.off.includes(s));
    const ranked = rankByRevenue(rows);
    const excluded = new Set<string>([
        ...ranked.slice(0, Math.max(0, rule.skipTop)),
        ...(rule.skipBottom > 0 ? ranked.slice(-rule.skipBottom) : []),
    ]);
    const out: CoSelection = {};
    const [lo, hi] = scoreRange(rule);
    for (const row of rows) {
        if (excluded.has(row.asin)) continue;
        const ticked = services.filter((s) => {
            if (locked.has(`${row.asin}:${s}`)) return false;
            const score = row.scores[s];
            return score !== null && score !== undefined && score >= lo && score <= hi;
        });
        const stacked = stackServices(row, ticked, locked);
        if (rule.skipCostly && servicesCost(stacked, rates) > (row.revenue12mo ?? 0)) continue;
        if (stacked.length > 0) out[row.asin] = stacked;
    }
    return out;
}

// A hand tick adds the service and everything before it; an untick removes
// the service and everything after it.
export function toggleCell(selection: CoSelection, row: CoAssortmentRow, service: CoService, locked: ReadonlySet<string> = new Set()): CoSelection {
    const { asin } = row;
    const current = selection[asin] ?? [];
    const at = CO_SERVICES.indexOf(service);
    let next: CoService[];
    if (current.includes(service)) next = current.filter((s) => CO_SERVICES.indexOf(s) < at);
    else if (tickable(row, service)) next = stackServices(row, [...current, service], locked);
    else return selection;
    const out = { ...selection };
    if (next.length === 0) delete out[asin];
    else out[asin] = next;
    return out;
}

export interface CartSummary {
    asins: number;
    cells: number;
    total: number;
    byService: Record<CoService, number>;
}

// Counts only the room's own ASINs (`known`), the same rule the order endpoint
// applies: a cart entry for an ASIN outside the assortment is never priced.
export function summarize(selection: CoSelection, rates: ContentOptimization["rates"] | undefined, known: ReadonlySet<string>): CartSummary {
    const r = { ...DEFAULT_CO_RATES, ...(rates ?? {}) };
    const byService = Object.fromEntries(CO_SERVICES.map((s) => [s, 0])) as Record<CoService, number>;
    let asins = 0;
    let cells = 0;
    let total = 0;
    for (const [asin, services] of Object.entries(selection)) {
        if (!known.has(asin)) continue;
        asins += 1;
        for (const s of services) {
            byService[s] += 1;
            cells += 1;
            total += r[s];
        }
    }
    return { asins, cells, total, byService };
}
